import AppModal from '@/components/AppModal/AppModal';
import BoldSmallText from '@/components/BoldSmallText';
import InformationBanner from '@/components/InformationBanner';
import RegularText from '@/components/RegularText';
import { openCrisp } from '@/helpers';
import { Banner, BlockStack, Box, Button, Icon, InlineStack, List, TextField } from '@shopify/polaris';
import { InfoIcon } from '@shopify/polaris-icons';
import { isEmpty } from 'lodash';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  googleMapApiKey: string;
  open: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onChange: (value: string) => void;
  onDismissInfo: () => void;
  handleDismissSuccess: () => void;
  handleDismissWarning: () => void;
  isVisible: boolean;
  loading?: boolean;
  disabledPrimary?: boolean;
  helpText?: string;
  isErrorShow?: string | boolean;
  isSuccessShow?: boolean;
  isWarningBannerShow?: boolean;
}

export const GoogleAPIModal: FC<Props> = ({
  open,
  onCancel,
  onSubmit,
  onDismissInfo,
  loading,
  isVisible,
  onChange,
  googleMapApiKey,
  helpText,
  handleDismissSuccess,
  handleDismissWarning,
  isErrorShow,
  isSuccessShow,
  isWarningBannerShow,
}) => {
  const { t } = useTranslation('general_settings');

  return (
    <AppModal
      open={open}
      onCancel={onCancel}
      onSubmit={onSubmit}
      title={t('sections.bannerGoogleAPI.title')}
      cancelText={t('sections.bannerGoogleAPI.cancelText')}
      submitText={t('sections.bannerGoogleAPI.submitText')}
      loading={loading}
      disabledPrimary={isEmpty(googleMapApiKey)}
      disabledCancel={loading}
    >
      <InformationBanner isVisible={isVisible} onDismiss={onDismissInfo}>
        <BlockStack gap={'200'}>
          <Box>
            <BoldSmallText>{t('sections.bannerGoogleAPI.bannerTitle')}</BoldSmallText>
            <RegularText>{t('sections.bannerGoogleAPI.bannerHelpText')}</RegularText>
          </Box>
          <Box>
            <Button
              onClick={() =>
                window.open('https://docs.dingdoong.io/how-to-enable-gg-maps-api-key/creating-a-google-maps-api-key', '_blank')
              }
            >
              {t('sections.bannerGoogleAPI.guide')}
            </Button>
          </Box>
        </BlockStack>
      </InformationBanner>
      {isVisible && <Box paddingBlockEnd={'400'} />}
      <TextField
        label={t('sections.bannerGoogleAPI.inputLabel')}
        value={googleMapApiKey}
        onChange={onChange}
        autoComplete="off"
        helpText={helpText}
        labelAction={{
          content: t('sections.bannerGoogleAPI.inputInstruction'),
          onAction: openCrisp,
        }}
      />
      {isErrorShow && (
        <BlockStack gap={'300'}>
          <Box color="text-critical-secondary" paddingBlockStart={'200'}>
            <InlineStack gap={'200'}>
              <Box>
                <Icon source={InfoIcon} />
              </Box>
              <RegularText>{t('sections.bannerGoogleAPI.inputError')}</RegularText>
            </InlineStack>
          </Box>
          {isWarningBannerShow && (
            <Banner tone="warning" onDismiss={handleDismissWarning}>
              <RegularText>{t('sections.bannerGoogleAPI.warning.title')}</RegularText>
              <List type="bullet">
                <List.Item>{t('sections.bannerGoogleAPI.warning.content')}</List.Item>
                <List.Item>
                  <RegularText>
                    Check that you have activated <BoldSmallText as="span">Maps, Routes</BoldSmallText> and{' '}
                    <BoldSmallText as="span">Places</BoldSmallText> in your Google Maps API settings
                  </RegularText>
                </List.Item>
              </List>
              <Box paddingBlockStart={'200'}>
                <Button
                  onClick={() =>
                    window.open(
                      'https://docs.dingdoong.io/how-to-enable-gg-maps-api-key/creating-a-google-maps-api-key#id-3.-use-case',
                      '_blank',
                    )
                  }
                >
                  {t('sections.bannerGoogleAPI.warning.read')}
                </Button>
              </Box>
            </Banner>
          )}
        </BlockStack>
      )}
      {isSuccessShow && (
        <Box paddingBlockStart={'200'}>
          <Banner tone="success" onDismiss={handleDismissSuccess}>
            {t('sections.bannerGoogleAPI.success')}
          </Banner>
        </Box>
      )}
    </AppModal>
  );
};
